import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SyncIcon from '@mui/icons-material/Sync';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useToast } from 'use-toast-mui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  copyBranchAccessGroupMember,
  createBranchAccessGroup,
  createBranchGroup,
  deleteBranchAccessGroup,
  getBranchAccessGroupDetail,
  listBranchAccessGroups,
  listBranchSports,
  listBranchTerminals,
  mapBranchAccessGroupTerminals,
  moveBranchAccessGroupMember,
  syncBranchAccessGroups,
  updateBranchAccessGroup,
} from '../../../api/branch-access-group';
import {
  convertBranchDoors,
  createBranchDoor,
  deleteBranchDoor,
  listBranchDoors,
  updateBranchDoor,
  updateBranchDoorRestriction,
  getDoorEntranceTime,
  saveDoorEntranceTime,
} from '../../../api/branch-door';
import EntranceTimeModal from '../../storeTabs/EntranceTimeModal/EntranceTimeModal';
import DoorGuideModal from './DoorGuideModal';
import { GuideButton } from '../../lesson/groupLesson/GroupLessonStyled';

// branch_sport_type 가 비어 있는 지점용 fallback. 백엔드 SportType enum 과 일치 유지.
const SPORT_FALLBACK = [
  'FITNESS',
  'PILATES',
  'BARRE_PILATES',
  'YOGA',
  'CROSSFIT',
  'GOLF',
  'SWIMMING',
  'BALL_GAMES',
  'BOXING',
  'DANCE',
  'CYCLE',
  'EDUCATION',
  'TANNING',
  'STRETCHING',
];

// 출입문 회원 목록에서 회원당 미리 보여줄 유효 상품 수
const DOOR_MEMBER_PRODUCT_PREVIEW = 3;

const fmtDate = v => v?.replace('T', ' ').slice(0, 19) ?? '-';

const parseTerminalText = text =>
  text
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

// 출입문 추가/수정 모달 공용 디자인 토큰·부품
const DOOR_MODAL_COLORS = {
  primary: '#4AB3BC',
  primaryDark: '#0F7F85',
  gray1: '#F5F5F5',
  gray2: '#E5E5E5',
  gray6: '#737373',
  black: '#141414',
};

const DoorFieldLabel = ({ children, required }) => (
  <Typography sx={{ fontSize: 13, fontWeight: 700, color: DOOR_MODAL_COLORS.black, mb: 0.75 }}>
    {children}
    {required && <Box component="span" sx={{ color: '#E53935', ml: 0.5 }}>*</Box>}
  </Typography>
);

const doorInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: 14,
    '&.Mui-focused fieldset': { borderColor: DOOR_MODAL_COLORS.primary },
  },
};

const DoorModalShell = ({ open, onClose, title, children, onSave, saveDisabled, saving }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 2,
        background: '#EAF6F7',
        borderBottom: '1px solid #DCEEEF',
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MeetingRoomIcon sx={{ color: DOOR_MODAL_COLORS.primaryDark }} />
        <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{title}</Typography>
      </Box>
      <IconButton size="small" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
    <DialogContent sx={{ px: 3, py: 2.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>{children}</Box>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
      <Button
        onClick={onClose}
        sx={{
          flex: 1,
          height: 44,
          borderRadius: '8px',
          border: `1px solid ${DOOR_MODAL_COLORS.gray2}`,
          color: DOOR_MODAL_COLORS.gray6,
          fontWeight: 600,
        }}>
        취소
      </Button>
      <Button
        variant="contained"
        disableElevation
        onClick={onSave}
        disabled={saveDisabled || saving}
        sx={{
          flex: 1,
          height: 44,
          borderRadius: '8px',
          fontWeight: 700,
          background: DOOR_MODAL_COLORS.primary,
          '&:hover': { background: DOOR_MODAL_COLORS.primaryDark },
        }}>
        저장
      </Button>
    </DialogActions>
  </Dialog>
);

// 출입문 체계(converted=true) 뷰 — 출입문 목록/추가/수정/출입 제한 토글.
const DoorAccessView = ({ branchId, token, commonAccessGroupCode }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [guideOpen, setGuideOpen] = useState(false);
  // 회원수 클릭 → 그 문의 출입권 보유 회원 목록
  const [memberDoor, setMemberDoor] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [doorName, setDoorName] = useState('');
  const [memo, setMemo] = useState('');
  const [terminalText, setTerminalText] = useState('');
  const [restricted, setRestricted] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editDoorName, setEditDoorName] = useState('');
  const [editMemo, setEditMemo] = useState('');
  const [editTerminalText, setEditTerminalText] = useState('');

  const doorsQuery = useQuery({
    queryKey: ['branch-doors', branchId],
    queryFn: () => listBranchDoors(branchId, token),
    enabled: !!branchId && !!token,
  });

  const invalidateDoors = () => queryClient.invalidateQueries({ queryKey: ['branch-doors', branchId] });

  const createMutation = useMutation({
    mutationFn: payload => createBranchDoor(payload, token),
    onSuccess: () => {
      invalidateDoors();
      setCreateOpen(false);
      toast.success('출입문을 추가했습니다.');
    },
    onError: e => toast.error(e?.message ?? '출입문 추가에 실패했습니다.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateBranchDoor(id, payload, token),
    onSuccess: () => {
      invalidateDoors();
      setEditTarget(null);
      toast.success('출입문 정보를 수정했습니다.');
    },
    onError: e => toast.error(e?.message ?? '출입문 수정에 실패했습니다.'),
  });

  const restrictionMutation = useMutation({
    mutationFn: ({ id, value }) => updateBranchDoorRestriction(id, value, token),
    onSuccess: () => {
      invalidateDoors();
      toast.success('출입 제한 설정을 변경했습니다.');
    },
    onError: e => toast.error(e?.message ?? '출입 제한 설정 변경에 실패했습니다.'),
  });

  // 출입문 삭제 — 확인 모달을 거친다. 비가역이라 경고를 강하게 둔다.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const deleteMutation = useMutation({
    mutationFn: id => deleteBranchDoor(id, token),
    onSuccess: () => {
      invalidateDoors();
      setDeleteTarget(null);
      toast.success('출입문을 삭제했습니다.');
    },
    onError: e => toast.error(e?.message ?? '출입문 삭제에 실패했습니다.'),
  });

  // 문별 커스텀 입장시간 (#91 후속) — 서버 상태(customEntranceTime)가 진실원천.
  const [doorTimeModal, setDoorTimeModal] = useState(null); // { doorId, preset }

  // 출입문도 branch_access_group 행이라 그룹 상세 API(코드 기준 회원 목록)를 그대로 쓴다.
  const doorMembersQuery = useQuery({
    queryKey: ['branch-access-group-detail', memberDoor?.id],
    queryFn: () => getBranchAccessGroupDetail(memberDoor.id, token),
    enabled: !!memberDoor?.id && !!token,
  });

  // 회원의 유효 수강권/이용권을 한 셀에 압축 표시.
  const renderDoorMemberProducts = m => {
    const items = [
      ...(m.validTickets ?? []).map(p => ({ ...p, kind: '수강권', color: 'info' })),
      ...(m.validPasses ?? []).map(p => ({ ...p, kind: '이용권', color: 'secondary' })),
    ];
    if (items.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          유효 상품 없음
        </Typography>
      );
    }
    // 상품이 많은 회원은 3건까지만 보여주고 나머지는 건수로 접는다 — 상세는 회원 상품 상세에서.
    const shown = items.slice(0, DOOR_MEMBER_PRODUCT_PREVIEW);
    const rest = items.length - shown.length;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {shown.map((p, i) => (
          <Box key={`${p.kind}-${i}`} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip label={p.kind} size="small" color={p.color} variant="outlined" />
            <Typography variant="caption">
              {p.productName ?? '-'}
              {p.expireDate ? ` ~${p.expireDate}` : ''}
              {p.usageCount != null ? ` · ${p.usedCount ?? 0}/${p.usageCount}회` : ''}
            </Typography>
          </Box>
        ))}
        {rest > 0 && (
          <Typography variant="caption" color="text.secondary">
            외 {rest}건
          </Typography>
        )}
      </Box>
    );
  };

  const doorTimeMutation = useMutation({
    mutationFn: ({ id, payload }) => saveDoorEntranceTime(id, payload, token),
    onSuccess: (res, { id }) => {
      invalidateDoors();
      setDoorTimeModal(null);
      setEditTarget(prev => (prev && prev.id === id
        ? { ...prev, customEntranceTime: !!res?.custom } : prev));
      toast.success(res?.custom ? '이 출입문의 별도 입장시간을 저장했습니다.' : '지점 공통 설정으로 되돌렸습니다.');
    },
    onError: e => toast.error(e?.message ?? '출입문 입장시간 저장에 실패했습니다.'),
  });

  // forceSeparate: 수정 모달 스위치 ON 등 "별도 설정" 의도가 명확할 때 별도 모드로 강제 프리셋.
  // 목록 칩 클릭은 현재 상태 그대로 열어 공통 문이면 '지점 공통과 동일' ON 으로 시작한다.
  const openDoorTimeModal = async (door, { forceSeparate = false } = {}) => {
    try {
      const cur = await getDoorEntranceTime(door.id, token);
      setDoorTimeModal({
        doorId: door.id,
        preset: {
          sameAsStore: forceSeparate ? false : !cur?.custom,
          entranceHours: cur?.entranceHours ?? [],
        },
      });
    } catch (e) {
      toast.error(e?.message ?? '출입문 입장시간 조회에 실패했습니다.');
    }
  };

  const handleDoorTimeToggle = (door, checked) => {
    if (checked) {
      openDoorTimeModal(door, { forceSeparate: true });
    } else {
      doorTimeMutation.mutate({ id: door.id, payload: { custom: false } });
    }
  };

  const handleDoorTimeSave = payload => {
    if (!doorTimeModal) return;
    doorTimeMutation.mutate({
      id: doorTimeModal.doorId,
      payload: {
        custom: !payload.sameAsStore,
        entranceHours: payload.sameAsStore ? [] : payload.entranceHours,
      },
    });
  };

  const handleCreateOpen = () => {
    setDoorName('');
    setMemo('');
    setTerminalText('');
    setRestricted(false);
    setCreateOpen(true);
  };

  const handleCreateSave = () => {
    if (!doorName.trim()) return;
    const terminalIds = parseTerminalText(terminalText);
    if (terminalIds.length === 0) return;
    createMutation.mutate({
      branchId,
      doorName: doorName.trim(),
      memo: memo.trim() || undefined,
      terminalIds,
      restricted,
    });
  };

  const handleEditOpen = door => {
    setEditTarget(door);
    setEditDoorName(door.doorName ?? '');
    setEditMemo(door.memo ?? '');
    setEditTerminalText((door.terminalIds ?? []).join(', '));
  };

  const handleEditSave = () => {
    if (!editTarget || !editDoorName.trim()) return;
    const terminalIds = parseTerminalText(editTerminalText);
    updateMutation.mutate({
      id: editTarget.id,
      payload: {
        doorName: editDoorName.trim(),
        memo: editMemo.trim(),
        terminalIds,
      },
    });
  };

  const handleRestrictionToggle = door => {
    restrictionMutation.mutate({ id: door.id, value: !door.restricted });
  };

  const doors = doorsQuery.data?.doors ?? [];
  const restrictedCount = doors.filter(d => d.restricted).length;
  const memberTotal = doors.reduce((acc, d) => acc + (d.memberCount ?? 0), 0);

  const headCellSx = {
    fontSize: 12,
    fontWeight: 700,
    color: '#8A8A8A',
    letterSpacing: '0.02em',
    background: '#F7F9FA',
    borderBottom: '1px solid #ECEFF1',
    py: 1.5,
  };
  const bodyCellSx = { borderBottom: '1px solid #F1F3F4', py: 1.75 };
  const actionIconSx = {
    color: '#8A8A8A',
    borderRadius: '8px',
    '&:hover': { color: DOOR_MODAL_COLORS.primaryDark, background: '#E3F2F3' },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: DOOR_MODAL_COLORS.black }}>
            출입문 관리
          </Typography>
          <Typography sx={{ fontSize: 13, color: DOOR_MODAL_COLORS.gray6, mt: 0.5 }}>
            안면인식 출입문과 상품 연결을 관리합니다. 출입 제한이 없는 문은 유효한 회원권만 있으면 누구나 지나갈 수 있습니다.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          <GuideButton onClick={() => setGuideOpen(true)}>📖 출입문 관리 가이드</GuideButton>
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={handleCreateOpen}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              px: 2,
              background: DOOR_MODAL_COLORS.primary,
              '&:hover': { background: DOOR_MODAL_COLORS.primaryDark },
            }}>
            출입문 추가
          </Button>
        </Box>
      </Box>

      {/* 요약 */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        {[
          { label: '출입문', value: `${doors.length}개`, icon: <MeetingRoomIcon sx={{ fontSize: 18 }} /> },
          { label: '출입 제한 문', value: `${restrictedCount}개`, icon: <LockOutlinedIcon sx={{ fontSize: 18 }} /> },
          { label: '출입권 보유 회원', value: `${memberTotal}명`, icon: <PeopleAltIcon sx={{ fontSize: 18 }} /> },
        ].map(stat => (
          <Box
            key={stat.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 2,
              py: 1.25,
              minWidth: 180,
              border: `1px solid ${DOOR_MODAL_COLORS.gray2}`,
              borderRadius: '12px',
              background: '#fff',
            }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#E3F2F3',
                color: DOOR_MODAL_COLORS.primaryDark,
              }}>
              {stat.icon}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: DOOR_MODAL_COLORS.gray6 }}>{stat.label}</Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: DOOR_MODAL_COLORS.black }}>
                {stat.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* 목록 */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${DOOR_MODAL_COLORS.gray2}`, borderRadius: '12px', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                {
                  label: '출입문',
                  hint: ['안면인식 단말이 달린 문 하나입니다.', '아래 작은 글씨는 메모입니다.'],
                },
                {
                  label: '단말',
                  hint: ['이 문에 등록된 안면인식 단말 번호입니다.', '번호는 관리자에게 문의하세요.'],
                },
                {
                  label: '출입 제한',
                  hint: [
                    '제한 없음 — 유효한 회원권만 있으면 누구나 출입',
                    '제한중 — 출입설정에서 연결한 상품을 가진 회원만 출입',
                    '',
                    '클릭해서 바꿉니다.',
                  ],
                },
                {
                  label: '출입 시간',
                  hint: [
                    '지점 공통 — 매장 설정의 입장시간을 따릅니다.',
                    '별도 설정 — 이 문만의 요일별 시간을 씁니다.',
                    '',
                    '클릭해서 설정합니다.',
                  ],
                },
                {
                  label: '회원',
                  hint: [
                    '이 문의 출입권을 가진 회원 수입니다.',
                    '',
                    '클릭 → 회원 목록',
                    '회원 클릭 → 회원 상품 상세',
                  ],
                },
                {
                  label: '관리',
                  hint: [
                    '수정 — 출입문명 · 메모 · 단말 번호',
                    '삭제 — 출입권 보유 회원이 없을 때만 가능',
                    '',
                    '삭제하면 되돌릴 수 없습니다.',
                  ],
                },
              ].map(col => (
                <TableCell key={col.label} sx={headCellSx} align="center">
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      position: 'relative',
                      left: col.label === '출입문' ? -40 : col.label === '단말' ? 24 : 0,
                    }}>
                    {col.label}
                    <Tooltip
                      title={
                        <Box sx={{ fontSize: 12, lineHeight: 1.7, py: 0.5, px: 0.25 }}>
                          {col.hint.map((line, i) =>
                            line === '' ? <Box key={i} sx={{ height: 6 }} /> : <Box key={i}>{line}</Box>,
                          )}
                        </Box>
                      }
                      arrow
                      placement="top">
                      <HelpOutlineIcon sx={{ fontSize: 14, color: '#B0B0B0', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {doors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                  <MeetingRoomIcon sx={{ fontSize: 36, color: '#D0D0D0', mb: 1 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: DOOR_MODAL_COLORS.gray6 }}>
                    등록된 출입문이 없습니다
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#A6A6A6', mt: 0.5 }}>
                    우측 상단 &apos;출입문 추가&apos;로 첫 출입문을 등록하세요.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              doors.map(door => (
                <TableRow key={door.id} hover sx={{ '&:hover': { background: '#FAFCFC' } }}>
                  <TableCell sx={bodyCellSx} align="center">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, textAlign: 'left', minWidth: 220, position: 'relative', left: -40 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: door.restricted ? '#FFF3E0' : '#E3F2F3',
                          color: door.restricted ? '#B26A00' : DOOR_MODAL_COLORS.primaryDark,
                        }}>
                        <MeetingRoomIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: DOOR_MODAL_COLORS.black }}>
                          {door.doorName ?? '-'}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: door.memo ? DOOR_MODAL_COLORS.gray6 : '#BDBDBD',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 360,
                          }}>
                          {door.memo || '메모 없음'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="center">
                    {(door.terminalIds ?? []).length === 0 ? (
                      <Typography sx={{ fontSize: 12, color: '#BDBDBD', position: 'relative', left: 24 }}>-</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', position: 'relative', left: 24 }}>
                        {(door.terminalIds ?? []).map(t => (
                          <Chip
                            key={t}
                            icon={<DeviceHubIcon sx={{ fontSize: 14 }} />}
                            label={t}
                            size="small"
                            sx={{
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              fontSize: 12,
                              fontWeight: 600,
                              background: DOOR_MODAL_COLORS.gray1,
                              color: '#555',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="center">
                    <Tooltip
                      title={
                        door.restricted
                          ? '연결한 상품을 가진 회원만 출입 (클릭해서 해제)'
                          : '유효한 회원권만 있으면 누구나 출입 (클릭해서 제한)'
                      }
                      arrow>
                      <Box
                        onClick={() => !restrictionMutation.isPending && handleRestrictionToggle(door)}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          pl: 0.5,
                          pr: 1.25,
                          py: 0.25,
                          borderRadius: '999px',
                          cursor: restrictionMutation.isPending ? 'default' : 'pointer',
                          background: door.restricted ? '#FFF3E0' : DOOR_MODAL_COLORS.gray1,
                          color: door.restricted ? '#B26A00' : DOOR_MODAL_COLORS.gray6,
                          fontSize: 12,
                          fontWeight: 700,
                          transition: 'all 0.15s ease',
                          '&:hover': { filter: 'brightness(0.97)' },
                        }}>
                        <Switch
                          size="small"
                          checked={!!door.restricted}
                          disabled={restrictionMutation.isPending}
                          onClick={e => e.stopPropagation()}
                          onChange={() => handleRestrictionToggle(door)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#EF6C00' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { background: '#EF6C00' },
                          }}
                        />
                        {door.restricted ? '제한중' : '제한 없음'}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="center">
                    <Tooltip title="클릭해서 이 출입문의 입장시간을 설정합니다" arrow>
                      <Chip
                        icon={<AccessTimeIcon sx={{ fontSize: 15 }} />}
                        label={door.customEntranceTime ? '별도 설정' : '지점 공통'}
                        size="small"
                        onClick={() => openDoorTimeModal(door)}
                        sx={{
                          fontWeight: 600,
                          cursor: 'pointer',
                          color: door.customEntranceTime ? '#B26A00' : DOOR_MODAL_COLORS.gray6,
                          background: door.customEntranceTime ? '#FFF3E0' : DOOR_MODAL_COLORS.gray1,
                          '& .MuiChip-icon': { color: 'inherit' },
                          '&:hover': {
                            background: door.customEntranceTime ? '#FFE0B2' : '#E3F2F3',
                            color: door.customEntranceTime ? '#B26A00' : DOOR_MODAL_COLORS.primaryDark,
                          },
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="center">
                    <Tooltip title="출입권 보유 회원 보기" arrow>
                      <Chip
                        icon={<PeopleAltIcon sx={{ fontSize: 15 }} />}
                        label={`${door.memberCount ?? 0}명`}
                        size="small"
                        onClick={() => setMemberDoor(door)}
                        sx={{
                          fontWeight: 700,
                          cursor: 'pointer',
                          color: door.memberCount ? DOOR_MODAL_COLORS.primaryDark : DOOR_MODAL_COLORS.gray6,
                          background: door.memberCount ? '#E3F2F3' : DOOR_MODAL_COLORS.gray1,
                          '& .MuiChip-icon': { color: 'inherit' },
                          '&:hover': { background: door.memberCount ? '#CDE9EB' : '#E5E5E5' },
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="center">
                    <Tooltip title="수정" arrow>
                      <IconButton size="small" onClick={() => handleEditOpen(door)} sx={actionIconSx}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        door.memberCount
                          ? '출입권 보유 회원이 있어 삭제할 수 없습니다'
                          : '출입문 삭제'
                      }
                      arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!!door.memberCount}
                          onClick={() => setDeleteTarget(door)}
                          sx={{
                            ...actionIconSx,
                            '&:hover': { color: '#C62828', background: '#FFEBEE' },
                          }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 출입문 추가 */}
      <DoorModalShell
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="출입문 추가"
        onSave={handleCreateSave}
        saveDisabled={!doorName.trim() || parseTerminalText(terminalText).length === 0}
        saving={createMutation.isPending}>
        <Box>
          <DoorFieldLabel required>출입문명</DoorFieldLabel>
          <TextField
            value={doorName}
            onChange={e => setDoorName(e.target.value)}
            placeholder="예: 1층 정문, 골프존 게이트"
            size="small"
            fullWidth
            sx={doorInputSx}
          />
        </Box>
        <Box>
          <DoorFieldLabel>메모</DoorFieldLabel>
          <TextField
            value={memo}
            onChange={e => setMemo(e.target.value)}
            placeholder="관리용 메모를 입력해 주세요."
            multiline
            minRows={2}
            fullWidth
            sx={doorInputSx}
          />
        </Box>
        <Box>
          <DoorFieldLabel required>단말 번호</DoorFieldLabel>
          <TextField
            value={terminalText}
            onChange={e => setTerminalText(e.target.value)}
            placeholder="단말 번호는 관리자에게 문의해 주세요."
            size="small"
            fullWidth
            sx={doorInputSx}
          />
        </Box>
        <Box
          sx={{
            border: `1px solid ${DOOR_MODAL_COLORS.gray2}`,
            borderRadius: '10px',
            px: 2,
            py: 1.5,
            background: DOOR_MODAL_COLORS.gray1,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>출입 제한 설정</Typography>
            <Switch checked={restricted} onChange={e => setRestricted(e.target.checked)} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontSize: 13, lineHeight: 1.6 }}>
            {restricted
              ? '켜면 이 문에 연결한 상품을 가진 회원만 출입할 수 있습니다. 저장 후 상품 등록·수정의 출입설정에서 이 문을 연결하세요.'
              : '끄면 어떤 상품을 구매했든 유효한 회원권만 있으면 출입할 수 있습니다.'}
          </Typography>
        </Box>
      </DoorModalShell>

      {/* 출입문 수정 */}
      <DoorModalShell
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        title="출입문 수정"
        onSave={handleEditSave}
        saveDisabled={!editDoorName.trim()}
        saving={updateMutation.isPending}>
        <Box>
          <DoorFieldLabel required>출입문명</DoorFieldLabel>
          <TextField
            value={editDoorName}
            onChange={e => setEditDoorName(e.target.value)}
            size="small"
            fullWidth
            sx={doorInputSx}
          />
        </Box>
        <Box>
          <DoorFieldLabel>메모</DoorFieldLabel>
          <TextField
            value={editMemo}
            onChange={e => setEditMemo(e.target.value)}
            placeholder="관리용 메모를 입력해 주세요."
            multiline
            minRows={2}
            fullWidth
            sx={doorInputSx}
          />
        </Box>
        <Box>
          <DoorFieldLabel>단말 번호</DoorFieldLabel>
          <TextField
            value={editTerminalText}
            onChange={e => setEditTerminalText(e.target.value)}
            placeholder="단말 번호는 관리자에게 문의해 주세요."
            size="small"
            fullWidth
            sx={doorInputSx}
          />
        </Box>
        {editTarget && (
          <Box
            sx={{
              border: `1px solid ${DOOR_MODAL_COLORS.gray2}`,
              borderRadius: '10px',
              px: 2,
              py: 1.5,
              background: DOOR_MODAL_COLORS.gray1,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>출입 시간</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {editTarget.customEntranceTime && (
                  <Button
                    size="small"
                    onClick={() => openDoorTimeModal(editTarget)}
                    sx={{ color: DOOR_MODAL_COLORS.primaryDark, fontWeight: 600 }}>
                    시간 수정
                  </Button>
                )}
                <Switch
                  checked={!!editTarget.customEntranceTime}
                  onChange={e => handleDoorTimeToggle(editTarget, e.target.checked)}
                  disabled={doorTimeMutation.isPending}
                />
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontSize: 13, lineHeight: 1.6 }}>
              {editTarget.customEntranceTime
                ? '이 출입문만 별도 입장시간을 따릅니다.'
                : '지점 공통 설정(별도 입장시간 없으면 영업시간)을 따릅니다.'}
            </Typography>
          </Box>
        )}
        {editTarget && (
          <Box
            sx={{
              border: `1px solid ${DOOR_MODAL_COLORS.gray2}`,
              borderRadius: '10px',
              px: 2,
              py: 1.5,
              background: DOOR_MODAL_COLORS.gray1,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>출입 제한</Typography>
              <Chip
                label={editTarget.restricted ? '제한중' : '제한 없음'}
                size="small"
                sx={{
                  fontWeight: 600,
                  color: editTarget.restricted ? '#B26A00' : DOOR_MODAL_COLORS.primaryDark,
                  background: editTarget.restricted ? '#FFF3E0' : '#E3F2F3',
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontSize: 13, lineHeight: 1.6 }}>
              {editTarget.restricted
                ? '이 문에 연결한 상품을 가진 회원만 출입할 수 있습니다. 상품 등록·수정의 출입설정에서 이 문을 연결하세요. 변경은 목록의 토글에서 합니다.'
                : '어떤 상품을 구매했든 유효한 회원권만 있으면 출입할 수 있습니다. 변경은 목록의 토글에서 합니다.'}
            </Typography>
          </Box>
        )}
      </DoorModalShell>

      {/* 문별 커스텀 입장시간 (#91 후속) — 휴무일은 지점 단위라 숨긴다 */}
      <EntranceTimeModal
        open={!!doorTimeModal}
        onClose={() => setDoorTimeModal(null)}
        initialEntranceTime={doorTimeModal?.preset}
        onSave={handleDoorTimeSave}
        title="출입문 입장시간"
        sameLabel="지점 공통 설정과 동일"
        sameHint="*끄면 이 출입문만 별도 입장시간을 따릅니다."
        hideHolidays
      />

      <DoorGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />

      {/* 출입문 삭제 확인 — 비가역 */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#C62828' }}>
          <DeleteIcon />
          출입문 삭제
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
            &apos;{deleteTarget?.doorName}&apos; 출입문을 삭제할까요?
          </Typography>
          <Box
            sx={{
              border: '1px solid #FFCDD2',
              background: '#FFEBEE',
              borderRadius: '10px',
              px: 2,
              py: 1.5,
            }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#C62828', mb: 0.5 }}>
              삭제하면 되돌릴 수 없습니다
            </Typography>
            <Typography sx={{ fontSize: 13, lineHeight: 1.7, color: '#424242' }}>
              • 안면인식 장비의 출입그룹과 구역이 함께 삭제됩니다.<br />
              • 이 문에 연결돼 있던 상품 설정이 해제됩니다.<br />
              • 복구가 필요하면 본사에 문의해 주세요.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            취소
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate(deleteTarget.id)}>
            {deleteMutation.isPending ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 회원수 클릭 — 이 문의 출입권 보유 회원. 행 클릭 시 회원 상품 상세로 이동 */}
      <Dialog open={!!memberDoor} onClose={() => setMemberDoor(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleAltIcon color="primary" />
          <span>{memberDoor?.doorName ?? '출입문'} — 출입권 보유 회원</span>
          <Chip
            label={`${(doorMembersQuery.data?.members ?? []).length}명`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            회원을 클릭하면 회원 상품 상세로 이동합니다. 유효 상품이 없는 회원은 출입 기간이 닫혀 있는 상태입니다.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>회원명</TableCell>
                  <TableCell>회원 ID</TableCell>
                  <TableCell>유효 수강권/이용권</TableCell>
                  <TableCell>UBio User ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(doorMembersQuery.data?.members ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {doorMembersQuery.isLoading ? '불러오는 중...' : '출입권을 가진 회원이 없습니다'}
                    </TableCell>
                  </TableRow>
                ) : (
                  doorMembersQuery.data.members.map(m => (
                    <TableRow
                      key={`${m.memberId}-${m.sportType ?? ''}`}
                      hover
                      onClick={() => navigate(`/member/${m.memberId}?tab=three`)}
                      sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{m.memberName ?? '-'}</TableCell>
                      <TableCell>{m.memberId}</TableCell>
                      <TableCell>{renderDoorMemberProducts(m)}</TableCell>
                      <TableCell>{m.accessUserId ?? '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberDoor(null)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// 종목 기반 출입그룹(converted=false) 뷰 — 기존 화면 그대로 + 상단 "출입문 체계로 전환" 버튼.
const LegacySportAccessGroupView = ({ branchId, token, onConverted, convertible }) => {
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  // 상세 모달 회원별 이동/복사 대상 그룹 (memberId-sportType -> toGroupId)
  const [moveTargets, setMoveTargets] = useState({});
  // 상세 모달 필터 — 종목(sportType) / 구분(TICKET|PASS)
  const [detailSportType, setDetailSportType] = useState('');
  const [detailCategory, setDetailCategory] = useState('');

  // 그룹(Group) 생성 폼
  const [groupCreateOpen, setGroupCreateOpen] = useState(false);
  const [branchGroupName, setBranchGroupName] = useState('');

  // 단말 매핑 폼 — 단말 ID 직접 입력(콤마 구분)
  const [mapTarget, setMapTarget] = useState(null);
  const [mapTerminalText, setMapTerminalText] = useState('');

  // 신규 폼
  const [sportType, setSportType] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedTerminals, setSelectedTerminals] = useState([]);

  // 수정 폼
  const [editTarget, setEditTarget] = useState(null);
  const [editCode, setEditCode] = useState('');
  const [editName, setEditName] = useState('');
  const [editTerminals, setEditTerminals] = useState([]);

  // 출입문 체계 전환 확인
  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);

  const groupsQuery = useQuery({
    queryKey: ['branch-access-groups', branchId],
    queryFn: () => listBranchAccessGroups(branchId, token),
    enabled: !!branchId && !!token,
  });

  const terminalsQuery = useQuery({
    queryKey: ['branch-terminals', branchId],
    queryFn: () => listBranchTerminals(branchId, token),
    enabled: !!branchId && !!token,
  });

  const sportsQuery = useQuery({
    queryKey: ['branch-sports', branchId],
    queryFn: () => listBranchSports(branchId, token),
    enabled: !!branchId && !!token,
  });

  const detailQuery = useQuery({
    queryKey: ['branch-access-group-detail', detailId, detailSportType, detailCategory],
    queryFn: () =>
      getBranchAccessGroupDetail(detailId, token, {
        sportType: detailSportType || undefined,
        category: detailCategory || undefined,
      }),
    enabled: !!detailId && !!token,
  });

  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: ['branch-access-groups', branchId] });

  // 그룹 수가 바뀌면 신규 모달의 sport 옵션(hasGroup) 도 갱신해야 함.
  const invalidateListAndSports = () => {
    invalidateList();
    queryClient.invalidateQueries({ queryKey: ['branch-sports', branchId] });
  };

  const createMutation = useMutation({
    mutationFn: payload => createBranchAccessGroup(payload, token),
    onSuccess: () => {
      invalidateListAndSports();
      setCreateOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateBranchAccessGroup(id, payload, token),
    onSuccess: () => {
      invalidateList();
      setEditOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: id => deleteBranchAccessGroup(id, token),
    onSuccess: invalidateListAndSports,
  });

  const createBranchGroupMutation = useMutation({
    mutationFn: () => createBranchGroup(branchId, branchGroupName.trim() || undefined, token),
    onSuccess: data => {
      invalidateListAndSports();
      setGroupCreateOpen(false);
      const n = data?.groups?.length ?? 0;
      window.alert(
        `UBio 그룹(ID ${data?.ubioGroupId ?? '-'})을 생성하고 종목 ${n}건 행을 준비했습니다.\n` +
          '이어서 "누락 종목 동기화"로 출입그룹·출입구역을 생성하세요.'
      );
    },
    onError: e => window.alert(`그룹 생성 실패: ${e?.message ?? '알 수 없는 오류'}`),
  });

  const syncMutation = useMutation({
    mutationFn: () => syncBranchAccessGroups(branchId, token),
    onSuccess: created => {
      invalidateListAndSports();
      const n = Array.isArray(created) ? created.length : 0;
      window.alert(
        n > 0
          ? `${n}개 종목의 출입그룹(AccessGroup)·출입구역(AccessArea)을 생성했습니다.\n단말은 "단말 매핑"으로 지정하세요.`
          : '동기화할 대상(출입그룹 미생성 종목)이 없습니다.'
      );
    },
    onError: e => window.alert(`동기화 실패: ${e?.message ?? '알 수 없는 오류'}`),
  });

  const mapMutation = useMutation({
    mutationFn: ({ id, terminalIds }) => mapBranchAccessGroupTerminals(id, terminalIds, token),
    onSuccess: () => {
      invalidateList();
      setMapTarget(null);
      window.alert('출입구역에 단말을 매핑했습니다.');
    },
    onError: e => window.alert(`단말 매핑 실패: ${e?.message ?? '알 수 없는 오류'}`),
  });

  const moveMutation = useMutation({
    mutationFn: payload => moveBranchAccessGroupMember(payload, token),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['branch-access-group-detail', detailId] });
      invalidateList();
      setMoveTargets(prev => {
        const next = { ...prev };
        delete next[`${variables.memberId}-${variables.sportType}`];
        return next;
      });
      window.alert('회원을 이동했습니다. (UBio 동기화 완료)');
    },
    onError: e => window.alert(`이동 실패: ${e?.message ?? '알 수 없는 오류'}`),
  });

  const copyMutation = useMutation({
    mutationFn: payload => copyBranchAccessGroupMember(payload, token),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['branch-access-group-detail', detailId] });
      invalidateList();
      setMoveTargets(prev => {
        const next = { ...prev };
        delete next[`${variables.memberId}-${variables.sportType}`];
        return next;
      });
      window.alert('회원을 복사했습니다. (대상 그룹 UBio 발급 완료)');
    },
    onError: e => window.alert(`복사 실패: ${e?.message ?? '알 수 없는 오류'}`),
  });

  const convertMutation = useMutation({
    mutationFn: () => convertBranchDoors(branchId, token),
    onSuccess: data => {
      setConvertConfirmOpen(false);
      window.alert(
        `출입문 체계로 전환했습니다.\n` +
          `흡수된 출입그룹: ${data?.absorbedDoors ?? 0}건, 상품 매핑 생성: ${data?.backfilledProducts ?? 0}건`
      );
      onConverted();
    },
    onError: e => window.alert(`전환 실패: ${e?.message ?? '알 수 없는 오류'}`),
  });

  // 한 그룹(code)에 종목이 섞일 수 있어, 이동은 (회원+종목) 단위로 식별한다.
  const handleMoveMember = (memberId, sportType) => {
    const toGroupId = moveTargets[`${memberId}-${sportType}`];
    if (!toGroupId) return;
    moveMutation.mutate({ memberId, fromGroupId: detailId, toGroupId, sportType });
  };

  // 복사 — 원본 출입권은 유지하고 선택한 대상 출입그룹에 출입권을 추가한다.
  const handleCopyMember = (memberId, sportType) => {
    const toGroupId = moveTargets[`${memberId}-${sportType}`];
    if (!toGroupId) return;
    copyMutation.mutate({ memberId, fromGroupId: detailId, toGroupId, sportType });
  };

  const closeDetail = () => {
    setDetailId(null);
    setMoveTargets({});
    setDetailSportType('');
    setDetailCategory('');
  };

  // 회원의 유효 수강권/이용권을 한 셀에 압축 표시.
  const renderValidProducts = m => {
    const items = [
      ...(m.validTickets ?? []).map(p => ({ ...p, kind: '수강권', color: 'info' })),
      ...(m.validPasses ?? []).map(p => ({ ...p, kind: '이용권', color: 'secondary' })),
    ];
    if (items.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {items.map((p, i) => (
          <Box
            key={`${p.kind}-${i}`}
            sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip label={p.kind} size="small" color={p.color} variant="outlined" />
            <Typography variant="caption">
              {p.productName ?? '-'}
              {(p.sportTypes ?? []).length > 0 ? ` (${p.sportTypes.join('/')})` : ''}
              {p.expireDate ? ` ~${p.expireDate}` : ''}
              {p.usageCount != null ? ` · ${p.usedCount ?? 0}/${p.usageCount}회` : ''}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const handleCreateOpen = () => {
    setSportType('');
    setGroupName('');
    setSelectedTerminals([]);
    setCreateOpen(true);
  };

  const handleGroupCreateOpen = () => {
    setBranchGroupName('');
    setGroupCreateOpen(true);
  };

  const handleMapOpen = g => {
    setMapTarget(g);
    setMapTerminalText((g.terminalIds ?? []).join(', '));
  };

  const handleMapSave = () => {
    const ids = parseTerminalText(mapTerminalText);
    if (!mapTarget || ids.length === 0) return;
    mapMutation.mutate({ id: mapTarget.id, terminalIds: ids });
  };

  const handleCreateSave = () => {
    if (!sportType || selectedTerminals.length === 0) return;
    createMutation.mutate({
      branchId,
      sportType,
      terminalIds: selectedTerminals,
      groupName: groupName.trim() || undefined,
    });
  };

  const handleEditOpen = g => {
    setEditTarget(g);
    setEditCode(g.accessGroupCode ?? '');
    setEditName(g.groupName ?? '');
    setEditTerminals(g.terminalIds ?? []);
    setEditOpen(true);
  };

  const handleEditSave = () => {
    if (!editTarget || editTerminals.length === 0) return;
    updateMutation.mutate({
      id: editTarget.id,
      payload: {
        accessGroupCode: editCode.trim() || undefined,
        groupName: editName,
        terminalIds: editTerminals,
      },
    });
  };

  const handleDelete = id => {
    if (!window.confirm('해당 출입그룹을 비활성화 하시겠습니까?')) return;
    deleteMutation.mutate(id);
  };

  const groups = groupsQuery.data ?? [];
  const terminals = terminalsQuery.data ?? [];
  const sports = sportsQuery.data ?? [];

  // 신규 모달 sport 옵션 — 대표 sport(있으면) 우선, 없으면 fallback. 이미 그룹 있는 sport 는 disable.
  const sportOptions =
    sports.length > 0
      ? sports.map(s => ({ value: s.sportType, disabled: s.hasGroup }))
      : SPORT_FALLBACK.map(v => ({ value: v, disabled: false }));

  // 상세 모달 종목 필터 옵션 — 이 지점의 출입그룹 종목, 없으면 fallback.
  const detailSportOptions =
    groups.length > 0 ? [...new Set(groups.map(g => g.sportType).filter(Boolean))] : SPORT_FALLBACK;

  const multiSelectHandler = setter => e =>
    setter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">안면 인식 출입그룹 관리</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* 출입문 체계 전환은 비가역이라 오픈 대상 지점(서버 화이트리스트)에서만 노출한다 */}
          {convertible && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<SwapHorizIcon />}
              onClick={() => setConvertConfirmOpen(true)}
              disabled={!branchId}>
              출입문 체계로 전환
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<GroupAddIcon />}
            onClick={handleGroupCreateOpen}
            disabled={!branchId || createBranchGroupMutation.isPending}>
            그룹 생성
          </Button>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={() => syncMutation.mutate()}
            disabled={!branchId || syncMutation.isPending}>
            누락 종목 동기화
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}>
            신규 매핑
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>출입그룹명</TableCell>
              <TableCell>운동 종목</TableCell>
              <TableCell>출입그룹 ID(AccessGroup)</TableCell>
              <TableCell>그룹 ID(Group)</TableCell>
              <TableCell>단말 ID</TableCell>
              <TableCell align="center">회원수</TableCell>
              <TableCell>등록일</TableCell>
              <TableCell align="center">관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  매핑이 없습니다
                </TableCell>
              </TableRow>
            ) : (
              groups.map(g => (
                <TableRow key={g.id} hover>
                  <TableCell>{g.groupName ?? '-'}</TableCell>
                  <TableCell>{g.sportType}</TableCell>
                  <TableCell>{g.accessGroupCode}</TableCell>
                  <TableCell>
                    {g.groupId ?? <Typography color="warning.main">UBio 동기화 대기</Typography>}
                  </TableCell>
                  <TableCell>{(g.terminalIds ?? []).join(', ') || '-'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="클릭하면 묶인 회원 목록 보기 · 그룹 이동" arrow>
                      <Chip
                        icon={<PeopleAltIcon />}
                        label={`${g.memberCount ?? 0}명`}
                        size="small"
                        color={g.memberCount ? 'primary' : 'default'}
                        variant={g.memberCount ? 'filled' : 'outlined'}
                        onClick={() => setDetailId(g.id)}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 600,
                          transition: 'box-shadow .15s, transform .15s',
                          '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{fmtDate(g.regDt)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      title="단말 매핑"
                      onClick={() => handleMapOpen(g)}>
                      <DeviceHubIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="수정" onClick={() => handleEditOpen(g)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="삭제" onClick={() => handleDelete(g.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 신규 매핑 */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>신규 출입그룹 매핑</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>운동 종목</InputLabel>
              <Select
                value={sportType}
                label="운동 종목"
                onChange={e => setSportType(e.target.value)}>
                {sportOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                    {opt.value}
                    {opt.disabled ? ' (이미 그룹 있음)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="그룹명 (미입력 시 지점명-종목)"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>단말 (복수 선택)</InputLabel>
              <Select
                multiple
                value={selectedTerminals}
                label="단말 (복수 선택)"
                onChange={multiSelectHandler(setSelectedTerminals)}
                renderValue={selected => selected.join(', ')}>
                {terminals.length === 0 ? (
                  <MenuItem disabled>등록된 단말이 없습니다</MenuItem>
                ) : (
                  terminals.map(t => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <Typography variant="caption" color="text.secondary">
              저장 시 우리 DB 매핑 + UBio 그룹 생성/단말 연동이 자동 진행됩니다. UBio 동기화는 잠시 후
              완료되며 실패 시 운영자에게 별도 표시됩니다.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={handleCreateSave}
            disabled={!sportType || selectedTerminals.length === 0 || createMutation.isPending}>
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 그룹(Group) 생성 — UBio Group + 종목별 행 생성 */}
      <Dialog
        open={groupCreateOpen}
        onClose={() => setGroupCreateOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>지점 그룹(Group) 생성</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="그룹명 (미입력 시 지점명)"
              value={branchGroupName}
              onChange={e => setBranchGroupName(e.target.value)}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              UBio에 지점 그룹(Group)을 생성하고, 지점의 모든 종목에 출입그룹 행을 만들어 GROUP_ID를
              기록합니다. 새로 설치한 UBio 지점도 여기서 그룹을 만든 뒤 "누락 종목 동기화"로
              출입그룹·출입구역을 생성하세요.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupCreateOpen(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={() => createBranchGroupMutation.mutate()}
            disabled={!branchId || createBranchGroupMutation.isPending}>
            생성
          </Button>
        </DialogActions>
      </Dialog>

      {/* 단말 매핑 — 출입구역(AccessArea)에 단말 push */}
      <Dialog open={!!mapTarget} onClose={() => setMapTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          단말 매핑 ({mapTarget?.sportType} / {mapTarget?.groupName ?? '-'})
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="단말 ID (콤마로 구분)"
              placeholder="예: 860101, 860103"
              value={mapTerminalText}
              onChange={e => setMapTerminalText(e.target.value)}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              입력한 단말이 이 출입그룹의 출입구역(AccessArea)에 매핑됩니다. 출입그룹이 아직 없으면 먼저
              "누락 종목 동기화"를 실행하세요.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapTarget(null)}>취소</Button>
          <Button
            variant="contained"
            onClick={handleMapSave}
            disabled={parseTerminalText(mapTerminalText).length === 0 || mapMutation.isPending}>
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 수정 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>출입그룹 수정 ({editTarget?.sportType})</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="access_group_code"
              value={editCode}
              onChange={e => setEditCode(e.target.value)}
              fullWidth
              helperText="변경 시 이 코드를 쓰는 회원 출입권이 함께 갱신됩니다(cascade)."
            />
            <TextField
              label="그룹명"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>단말 (복수 선택)</InputLabel>
              <Select
                multiple
                value={editTerminals}
                label="단말 (복수 선택)"
                onChange={multiSelectHandler(setEditTerminals)}
                renderValue={selected => selected.join(', ')}>
                {terminals.length === 0 ? (
                  <MenuItem disabled>등록된 단말이 없습니다</MenuItem>
                ) : (
                  terminals.map(t => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editTerminals.length === 0 || updateMutation.isPending}>
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 상세 — 묶인 회원 + 유효 수강권/이용권 + 그룹 이동/복사 */}
      <Dialog open={!!detailId} onClose={closeDetail} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleAltIcon color="primary" />
          <span>{detailQuery.data?.group?.groupName ?? '출입그룹'} — 묶인 회원</span>
          <Chip
            label={`${(detailQuery.data?.members ?? []).length}명`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 1, mt: 1 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>종목 필터</InputLabel>
              <Select
                value={detailSportType}
                label="종목 필터"
                onChange={e => setDetailSportType(e.target.value)}>
                <MenuItem value="">전체</MenuItem>
                {detailSportOptions.map(v => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>구분 필터</InputLabel>
              <Select
                value={detailCategory}
                label="구분 필터"
                onChange={e => setDetailCategory(e.target.value)}>
                <MenuItem value="">전체</MenuItem>
                <MenuItem value="TICKET">수강권</MenuItem>
                <MenuItem value="PASS">이용권</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>회원명</TableCell>
                  <TableCell>회원 ID</TableCell>
                  <TableCell>종목</TableCell>
                  <TableCell>유효 수강권/이용권</TableCell>
                  <TableCell>UBio User ID</TableCell>
                  <TableCell>다른 그룹으로 이동/복사</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detailQuery.data?.members ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {detailQuery.isLoading ? '불러오는 중...' : '묶인 회원이 없습니다'}
                    </TableCell>
                  </TableRow>
                ) : (
                  detailQuery.data.members.map(m => (
                    <TableRow key={`${m.memberId}-${m.sportType}`}>
                      <TableCell>{m.memberName ?? '-'}</TableCell>
                      <TableCell>{m.memberId}</TableCell>
                      <TableCell>
                        {m.sportType ? (
                          <Chip label={m.sportType} size="small" variant="outlined" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{renderValidProducts(m)}</TableCell>
                      <TableCell>{m.accessUserId ?? '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <Select
                              displayEmpty
                              value={moveTargets[`${m.memberId}-${m.sportType}`] ?? ''}
                              onChange={e =>
                                setMoveTargets(prev => ({
                                  ...prev,
                                  [`${m.memberId}-${m.sportType}`]: e.target.value,
                                }))
                              }
                              renderValue={selected => {
                                if (!selected) {
                                  return (
                                    <Typography color="text.secondary">대상 출입그룹 선택</Typography>
                                  );
                                }
                                const g = groups.find(x => x.id === selected);
                                return g?.groupName || g?.sportType || g?.accessGroupCode || selected;
                              }}>
                              <MenuItem value="">
                                <Typography color="text.secondary">선택 안 함</Typography>
                              </MenuItem>
                              {groups
                                .filter(g => g.id !== detailId)
                                .map(g => (
                                  <MenuItem key={g.id} value={g.id}>
                                    <Box
                                      sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                                      <Typography variant="body2" fontWeight={600}>
                                        {g.groupName || '(이름 없음)'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {g.sportType}
                                        {g.accessGroupCode ? ` · ${g.accessGroupCode}` : ''}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            startIcon={<ArrowForwardIcon />}
                            disabled={
                              !moveTargets[`${m.memberId}-${m.sportType}`] ||
                              moveMutation.isPending ||
                              copyMutation.isPending
                            }
                            onClick={() => handleMoveMember(m.memberId, m.sportType)}>
                            이동
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            disabled={
                              !moveTargets[`${m.memberId}-${m.sportType}`] ||
                              moveMutation.isPending ||
                              copyMutation.isPending
                            }
                            onClick={() => handleCopyMember(m.memberId, m.sportType)}>
                            복사
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            이동은 원본에서 빼서 옮기고, 복사는 원본을 둔 채 대상 그룹에도 추가합니다(양쪽 단말 출입). 둘 다
            UBio가 즉시 반영되며(기존 출입기간 유지), 대상 그룹에 이미 출입권이 있으면 수행할 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 출입문 체계 전환 확인 */}
      <Dialog open={convertConfirmOpen} onClose={() => setConvertConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>출입문 체계로 전환하시겠습니까?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            기존 종목 그룹이 전부 &apos;출입 제한&apos; 출입문으로 전환되고 기존 상품에 매핑이 자동
            생성됩니다.
            <br />
            <strong>되돌릴 수 없습니다.</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertConfirmOpen(false)}>취소</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => convertMutation.mutate()}
            disabled={convertMutation.isPending}>
            전환
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const FaceAccessGroup = () => {
  const { user } = useSelector(state => state.user);
  const branchId = user?.branch?.branchId;
  const token = user?.token;

  const doorStatusQuery = useQuery({
    queryKey: ['branch-doors', branchId],
    queryFn: () => listBranchDoors(branchId, token),
    enabled: !!branchId && !!token,
  });

  if (doorStatusQuery.data?.converted) {
    return (
      <DoorAccessView
        branchId={branchId}
        token={token}
        commonAccessGroupCode={doorStatusQuery.data?.commonAccessGroupCode}
      />
    );
  }

  return (
    <LegacySportAccessGroupView
      branchId={branchId}
      token={token}
      convertible={doorStatusQuery.data?.convertible === true}
      onConverted={() => doorStatusQuery.refetch()}
    />
  );
};

export default FaceAccessGroup;
